/**
 * A QR encoder, byte mode, error correction level M.
 *
 * Written here rather than pulled in, for the same reason `escpos.ts` writes
 * out CP866 by hand: this package has one dependency and a receipt renderer is
 * not the place to acquire a second. The scope is exactly what a receipt needs
 * — one byte-mode segment at level M — and not a general encoder.
 *
 * Level M is e-Barimt's own choice and the right one for thermal paper: 15%
 * recovery covers the smearing and the fold, where L does not and Q spends
 * modules a 72mm slip cannot afford.
 *
 * The text renderer does not use this. A thermal head encodes its own QR from
 * `GS ( k`, which is smaller and sharper than a bitmap of one — see `escpos.ts`.
 */

/** A square of modules. `true` is dark. */
export interface QrCode {
  readonly size: number
  readonly modules: readonly boolean[]
  readonly version: number
}

export class QrTooLong extends Error {
  constructor(bytes: number) {
    super(`${bytes} bytes will not fit in a version 40 code at level M`)
    this.name = 'QrTooLong'
  }
}

/**
 * Per version: EC codewords per block, then the two block groups as
 * (count, data codewords). Level M only.
 */
const BLOCKS: readonly (readonly [number, number, number, number, number])[] = [
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
  [30, 1, 50, 4, 51],
  [22, 6, 36, 2, 37],
  [22, 8, 37, 1, 38],
  [24, 4, 40, 5, 41],
  [24, 5, 41, 5, 42],
  [28, 7, 45, 3, 46],
  [28, 10, 46, 1, 47],
  [26, 9, 43, 4, 44],
  [26, 3, 44, 11, 45],
  [26, 3, 41, 13, 42],
  [26, 17, 42, 0, 0],
  [28, 17, 46, 0, 0],
  [28, 4, 47, 14, 48],
  [28, 6, 45, 14, 46],
  [28, 8, 47, 13, 48],
  [28, 19, 46, 4, 47],
  [28, 22, 45, 3, 46],
  [28, 3, 45, 23, 46],
  [28, 21, 45, 7, 46],
  [28, 19, 47, 10, 48],
  [28, 2, 46, 29, 47],
  [28, 10, 46, 23, 47],
  [28, 14, 46, 21, 47],
  [28, 14, 46, 23, 47],
  [28, 12, 47, 26, 48],
  [28, 6, 47, 34, 48],
  [28, 29, 46, 14, 47],
  [28, 13, 46, 32, 47],
  [28, 40, 47, 7, 48],
  [28, 18, 47, 31, 48]
]

/** Alignment-pattern centre coordinates, versions 2-40. */
const ALIGN: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170]
]

// GF(256) with the QR primitive polynomial x^8+x^4+x^3+x^2+1.
const EXP = new Uint8Array(512)
const LOG = new Uint8Array(256)
{
  let x = 1
  for (let i = 0; i < 255; i++) {
    EXP[i] = x
    LOG[x] = i
    x <<= 1
    if ((x & 0x100) !== 0) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!
}

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!)

function generator(degree: number): Uint8Array {
  let poly = Uint8Array.from([1])
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1)
    for (let j = 0; j < poly.length; j++) {
      next[j] = next[j]! ^ poly[j]!
      next[j + 1] = next[j + 1]! ^ mul(poly[j]!, EXP[i]!)
    }
    poly = next
  }
  return poly
}

/** The Reed-Solomon remainder — the error-correction codewords for one block. */
function remainder(data: Uint8Array, degree: number): Uint8Array {
  const gen = generator(degree)
  const rem = new Uint8Array(degree)
  for (const byte of data) {
    const factor = byte ^ rem[0]!
    rem.copyWithin(0, 1)
    rem[degree - 1] = 0
    for (let j = 0; j < degree; j++) rem[j] = rem[j]! ^ mul(gen[j + 1]!, factor)
  }
  return rem
}

class Bits {
  readonly bytes: number[] = []
  length = 0

  push(value: number, count: number): void {
    for (let i = count - 1; i >= 0; i--) {
      if (this.length % 8 === 0) this.bytes.push(0)
      if (((value >>> i) & 1) !== 0) this.bytes[this.bytes.length - 1]! |= 0x80 >>> (this.length % 8)
      this.length++
    }
  }
}

const dataCodewords = (version: number): number => {
  const [, g1, d1, g2, d2] = BLOCKS[version - 1]!
  return g1 * d1 + g2 * d2
}

/** 8-bit character counts below version 10, 16-bit at and above it. */
const countBits = (version: number): number => (version < 10 ? 8 : 16)

function pickVersion(byteLength: number): number {
  for (let version = 1; version <= 40; version++) {
    const needed = 4 + countBits(version) + byteLength * 8
    if (needed <= dataCodewords(version) * 8) return version
  }
  throw new QrTooLong(byteLength)
}

/** Mode indicator, length, payload, terminator, then the 0xEC/0x11 padding. */
function payloadCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const capacity = dataCodewords(version)
  const bits = new Bits()
  bits.push(0b0100, 4)
  bits.push(bytes.length, countBits(version))
  for (const byte of bytes) bits.push(byte, 8)
  bits.push(0, Math.min(4, capacity * 8 - bits.length))
  if (bits.length % 8 !== 0) bits.push(0, 8 - (bits.length % 8))

  const out = new Uint8Array(capacity)
  out.set(bits.bytes.slice(0, capacity))
  for (let i = bits.bytes.length, pad = 0; i < capacity; i++, pad++) out[i] = pad % 2 === 0 ? 0xec : 0x11
  return out
}

/**
 * Split into blocks, error-correct each, then interleave.
 *
 * The interleave is what makes a burst of damage — a fold, a thumb — land one
 * codeword deep in many blocks instead of wiping out one block entirely.
 */
function interleave(payload: Uint8Array, version: number): Uint8Array {
  const [ecPerBlock, g1, d1, g2, d2] = BLOCKS[version - 1]!
  const data: Uint8Array[] = []
  const ec: Uint8Array[] = []

  let offset = 0
  for (let i = 0; i < g1 + g2; i++) {
    const size = i < g1 ? d1 : d2
    const block = payload.subarray(offset, offset + size)
    offset += size
    data.push(block)
    ec.push(remainder(block, ecPerBlock))
  }

  const out: number[] = []
  for (let i = 0; i < Math.max(d1, d2); i++) {
    for (const block of data) if (i < block.length) out.push(block[i]!)
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ec) out.push(block[i]!)
  }
  return Uint8Array.from(out)
}

interface Grid {
  size: number
  modules: boolean[]
  /** Function patterns and format areas, which the data stream skips. */
  fixed: boolean[]
}

function blank(version: number): Grid {
  const size = version * 4 + 17
  return {
    size,
    modules: new Array<boolean>(size * size).fill(false),
    fixed: new Array<boolean>(size * size).fill(false)
  }
}

function set(grid: Grid, row: number, column: number, dark: boolean, fixed = true): void {
  if (row < 0 || column < 0 || row >= grid.size || column >= grid.size) return
  grid.modules[row * grid.size + column] = dark
  if (fixed) grid.fixed[row * grid.size + column] = true
}

function finder(grid: Grid, row: number, column: number): void {
  // The 7x7 eye plus the one-module separator around it, drawn together so the
  // separator cannot be forgotten on the edges where it falls outside.
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const inner = r >= 2 && r <= 4 && c >= 2 && c <= 4
      const ring = r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6)
      set(grid, row + r, column + c, inner || ring)
    }
  }
}

function patterns(grid: Grid, version: number): void {
  const last = grid.size - 7
  finder(grid, 0, 0)
  finder(grid, 0, last)
  finder(grid, last, 0)

  for (let i = 8; i < grid.size - 8; i++) {
    set(grid, 6, i, i % 2 === 0)
    set(grid, i, 6, i % 2 === 0)
  }

  const centres = ALIGN[version - 1]!
  for (const row of centres) {
    for (const column of centres) {
      // The three corners already carry finders.
      const onFinder =
        (row === 6 && column === 6) ||
        (row === 6 && column === grid.size - 7) ||
        (row === grid.size - 7 && column === 6)
      if (onFinder) continue
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          set(grid, row + r, column + c, Math.max(Math.abs(r), Math.abs(c)) !== 1)
        }
      }
    }
  }

  // Reserve the format areas so the data stream steps over them.
  for (const [row, column] of formatCells(grid.size)) set(grid, row, column, false)

  // The dark module, which is always dark and never masked. Written after the
  // reservation above, because it sits at the bottom of that same column and
  // reserving seven cells rather than eight is the difference between setting
  // it and clearing it again.
  set(grid, 4 * version + 9, 8, true)

  if (version >= 7) {
    let value = version
    for (let i = 0; i < 12; i++) value = (value << 1) ^ (((value >>> 11) & 1) !== 0 ? 0x1f25 : 0)
    const bits = (version << 12) | value
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0
      set(grid, Math.floor(i / 3), grid.size - 11 + (i % 3), dark)
      set(grid, grid.size - 11 + (i % 3), Math.floor(i / 3), dark)
    }
  }
}

/** Up one two-wide column, down the next, skipping the vertical timing line. */
function placeData(grid: Grid, codewords: Uint8Array): void {
  let bit = 0
  let upward = true
  for (let right = grid.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let step = 0; step < grid.size; step++) {
      const row = upward ? grid.size - 1 - step : step
      for (const column of [right, right - 1]) {
        if (grid.fixed[row * grid.size + column] === true) continue
        const byte = codewords[bit >>> 3]
        // Past the payload the remainder bits stay light, which is what the
        // specification calls for.
        set(grid, row, column, byte !== undefined && ((byte >>> (7 - (bit & 7))) & 1) !== 0, false)
        bit++
      }
    }
    upward = !upward
  }
}

const MASKS: readonly ((row: number, column: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0
]

function formatBits(mask: number): number {
  // Level M is 0b00 in the two high bits.
  const data = mask
  let value = data
  for (let i = 0; i < 10; i++) value = (value << 1) ^ (((value >>> 9) & 1) !== 0 ? 0x537 : 0)
  return ((data << 10) | value) ^ 0x5412
}

/**
 * Where the fifteen format bits live, twice over, least-significant first.
 *
 * Written out rather than derived, and the reservation in `patterns` is derived
 * from *this* rather than from a second closed form. Both copies step around
 * the timing row and column and around the dark module; a loop that reserves
 * one cell too many clears a timing module or the dark module, and the result
 * is a code that still looks like a QR and scans as nothing.
 */
function formatPositions(size: number): { first: readonly [number, number][]; second: readonly [number, number][] } {
  const first: [number, number][] = []
  for (let i = 0; i <= 5; i++) first.push([i, 8])
  first.push([7, 8], [8, 8], [8, 7])
  for (let i = 9; i < 15; i++) first.push([8, 14 - i])

  const second: [number, number][] = []
  for (let i = 0; i < 8; i++) second.push([8, size - 1 - i])
  for (let i = 8; i < 15; i++) second.push([size - 15 + i, 8])

  return { first, second }
}

/** Every cell the format bits own, plus the dark module below the second copy. */
function formatCells(size: number): [number, number][] {
  const { first, second } = formatPositions(size)
  return [...first, ...second, [size - 8, 8]]
}

function writeFormat(grid: Grid, mask: number): void {
  const bits = formatBits(mask)
  const { first, second } = formatPositions(grid.size)
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >>> i) & 1) !== 0
    set(grid, first[i]![0], first[i]![1], dark)
    set(grid, second[i]![0], second[i]![1], dark)
  }
}

/**
 * The four penalty rules, summed. The lowest-scoring mask is the one used.
 *
 * Mask choice is a *legibility* heuristic, not a correctness property — all
 * eight masks decode to the same bytes, and a scanner will read any of them.
 * The scoring here follows `qrcode@1.5.4`, the encoder the Electron app used,
 * so this package prints the codes the shops have been printing rather than
 * merely equivalent ones. That is also what lets `receipt.test.ts` assert
 * module-for-module equality against it instead of "some valid code".
 */
function penalty(grid: Grid): number {
  const { size, modules } = grid
  const at = (r: number, c: number): boolean => modules[r * size + c]!
  let score = 0

  // Rule 1: runs of five or more of one shade, in both directions. Three
  // points for the fifth module and one for every module after it.
  for (let i = 0; i < size; i++) {
    for (const horizontal of [true, false]) {
      let run = 1
      for (let j = 1; j < size; j++) {
        const previous = horizontal ? at(i, j - 1) : at(j - 1, i)
        const current = horizontal ? at(i, j) : at(j, i)
        if (current === previous) {
          run++
          if (run === 5) score += 3
          else if (run > 5) score += 1
        } else run = 1
      }
    }
  }

  // Rule 2: every 2x2 block of one shade.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const first = at(r, c)
      if (at(r, c + 1) === first && at(r + 1, c) === first && at(r + 1, c + 1) === first) score += 3
    }
  }

  // Rule 3: the finder-lookalike — 1:1:3:1:1 with four light modules on one
  // side, which is what makes a scanner think it has found an eye. Read as an
  // eleven-module sliding window in each direction, counting both orientations
  // separately, because a run with light on *both* sides is two false eyes.
  for (let i = 0; i < size; i++) {
    let row = 0
    let column = 0
    for (let j = 0; j < size; j++) {
      row = ((row << 1) & 0x7ff) | (at(i, j) ? 1 : 0)
      column = ((column << 1) & 0x7ff) | (at(j, i) ? 1 : 0)
      if (j < 10) continue
      if (row === 0x5d0 || row === 0x05d) score += 40
      if (column === 0x5d0 || column === 0x05d) score += 40
    }
  }

  // Rule 4: how far the proportion of dark modules strays from half.
  const dark = modules.reduce((n, m) => n + (m ? 1 : 0), 0)
  score += Math.abs(Math.ceil((dark * 100) / modules.length / 5) - 10) * 10
  return score
}

/**
 * Encode `text` as UTF-8 in a byte-mode, level-M code.
 *
 * `mask` forces one of the eight patterns instead of scoring for the best. It
 * exists for the tests, which compare module-for-module against a reference
 * encoder and need to separate a placement bug from a scoring one.
 */
export function encodeQr(text: string, options: { mask?: number } = {}): QrCode {
  const bytes = new TextEncoder().encode(text)
  const version = pickVersion(bytes.length)
  const codewords = interleave(payloadCodewords(bytes, version), version)

  let best: Grid | undefined
  let bestScore = Number.POSITIVE_INFINITY
  const candidates = options.mask === undefined ? [0, 1, 2, 3, 4, 5, 6, 7] : [options.mask]
  for (const mask of candidates) {
    const grid = blank(version)
    patterns(grid, version)
    placeData(grid, codewords)
    for (let r = 0; r < grid.size; r++) {
      for (let c = 0; c < grid.size; c++) {
        if (grid.fixed[r * grid.size + c] === true) continue
        if (MASKS[mask]!(r, c)) grid.modules[r * grid.size + c] = !grid.modules[r * grid.size + c]!
      }
    }
    writeFormat(grid, mask)
    const score = penalty(grid)
    if (score < bestScore) {
      bestScore = score
      best = grid
    }
  }

  const chosen = best!
  return { size: chosen.size, modules: chosen.modules, version }
}
